import React, { useEffect, useState } from 'react';
import { Badge } from '../ui';
import { describeSession, type LiveSession, type SessionState } from './albumSessionStatus';

const BADGE: Record<SessionState, { variant: 'success' | 'info' | 'danger'; label: string }> = {
  running: { variant: 'success', label: 'Running' },
  finished: { variant: 'info', label: 'Finished' },
  stopped: { variant: 'danger', label: 'Stopped' },
};

interface Props {
  live: LiveSession | null;
  /** Fixed clock for tests; the panel uses the real one otherwise. */
  now?: number;
}

/** What the saved session is doing, shown as soon as it is switched on (AUTO-099). */
export const AlbumSessionStatusPanel: React.FC<Props> = ({ live, now }) => {
  const [, setTick] = useState(0);
  const running = !!live?.enabled;

  // Re-render every 15 seconds while running so "3 minutes ago" and the stuck warning stay true between polls.
  useEffect(() => {
    if (!running || now !== undefined) return;
    const id = setInterval(() => setTick(t => t + 1), 15_000);
    return () => clearInterval(id);
  }, [running, now]);

  const status = describeSession(live, now ?? Date.now());
  if (!status || !live) return null;

  const lastRun = live.lastRunAt && !Number.isNaN(new Date(live.lastRunAt).getTime())
    ? new Date(live.lastRunAt).toLocaleTimeString()
    : '—';
  const badge = BADGE[status.state];

  return (
    <div className="mt-4 pt-4 border-t border-slate-800/60 space-y-3 text-xs" data-testid="album-session-status">
      <div className="flex items-center gap-2 flex-wrap" role="status">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <span className="text-sm font-semibold text-slate-100">{status.headline}</span>
      </div>

      {status.lines.length > 0 && (
        <ul className="space-y-1 text-slate-400">
          {status.lines.map(line => <li key={line}>{line}</li>)}
        </ul>
      )}

      {status.warning && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-950/30 p-3 text-amber-200" role="alert">
          {status.warning}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest">Tracks</div>
          <div className="text-slate-100 font-mono">{status.completed} / {status.target}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest">Spent</div>
          <div className="text-slate-100 font-mono">${(live.sessionSpentUsd ?? 0).toFixed(2)} / ${live.budgetCapUsd.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest">Failed</div>
          <div className={`font-mono ${(live.sessionFailed ?? 0) > 0 ? 'text-red-400' : 'text-slate-100'}`}>{live.sessionFailed ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest">Last Run</div>
          <div className="text-slate-100 font-mono">{lastRun}</div>
        </div>
      </div>

      {status.lastError && (
        <div className={`text-[11px] ${status.endedNormally ? 'text-emerald-300' : 'text-red-300'}`}>
          <strong>{status.endedNormally ? 'Session complete:' : 'Last error:'}</strong> {status.lastError}
        </div>
      )}
    </div>
  );
};
